import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiPlay, FiCheck, FiArrowRight, FiStar, FiUsers, FiTrendingUp, FiShield } from 'react-icons/fi';

const LandingPage = ({ onGetStarted }) => {
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      icon: FiTrendingUp,
      title: "2025 Pricing Database",
      description: "Updated material and labor costs with MasterFormat codes"
    },
    {
      icon: FiCheck,
      title: "Instant Specifications",
      description: "Generate professional C3030 ceiling assembly specs in minutes"
    },
    {
      icon: FiUsers,
      title: "Supplier Integration",
      description: "Direct connections to verified Philippine construction suppliers"
    },
    {
      icon: FiShield,
      title: "Standards Compliant",
      description: "Follows Philippine building codes and industry standards"
    }
  ];

  const testimonials = [
    {
      name: "Maria Santos",
      role: "Project Manager",
      company: "CLC Construction",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=maria",
      quote: "BQ SyncSpec cut our specification time by 80%. The pricing accuracy is outstanding."
    },
    {
      name: "Roberto Cruz",
      role: "Architect",
      company: "Cruz & Associates",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=roberto",
      quote: "Finally, a tool that understands Philippine construction standards. Game changer!"
    },
    {
      name: "Jennifer Lim",
      role: "Quantity Surveyor",
      company: "BuildPro Solutions",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=jennifer",
      quote: "The commercial quantity calculations are spot-on. Saves hours of manual work."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <motion.header 
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex justify-between items-center p-4 md:p-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">BQ</span>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">BQ SyncSpec</h1>
              <p className="text-xs text-slate-400">C3030 Ceiling Assemblies</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGetStarted}
            className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl text-sm font-medium border border-white/20 hover:bg-white/20 transition-all"
          >
            Sign In
          </motion.button>
        </motion.header>

        {/* Hero Section */}
        <section className="px-4 md:px-6 py-8 md:py-16 text-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Construction
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">
                Specs & Costing
              </span>
              Made Simple
            </h2>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Generate accurate ceiling assembly specifications and cost estimates for Philippine construction projects in minutes, not hours.
            </p>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,156,166,0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={onGetStarted}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-primary-dark rounded-2xl text-lg font-semibold shadow-2xl hover:shadow-primary/25 transition-all"
            >
              <SafeIcon icon={FiPlay} />
              Get Started Free
              <SafeIcon icon={FiArrowRight} />
            </motion.button>

            <p className="text-sm text-slate-400 mt-4">
              No credit card required • Start in seconds • Philippine standards compliant
            </p>
          </motion.div>
        </section>

        {/* Features Showcase */}
        <section className="px-4 md:px-6 py-8 md:py-16">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h3 className="text-2xl md:text-4xl font-bold mb-4">
                Everything You Need for Professional Specs
              </h3>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                Built specifically for Philippine construction professionals who demand accuracy and efficiency.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className={`p-6 rounded-2xl backdrop-blur-sm border transition-all ${
                    currentFeature === index 
                      ? 'bg-primary/20 border-primary/50 shadow-lg shadow-primary/25' 
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    currentFeature === index ? 'bg-primary' : 'bg-white/10'
                  }`}>
                    <SafeIcon icon={feature.icon} size={24} />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">{feature.title}</h4>
                  <p className="text-slate-300 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-8 md:py-16 bg-white/5 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h3 className="text-2xl md:text-4xl font-bold mb-4">
                Trusted by Construction Professionals
              </h3>
              <div className="flex justify-center items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current" />
                ))}
                <span className="ml-2 text-slate-300">4.9/5 from 200+ professionals</span>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full bg-primary/20"
                    />
                    <div>
                      <h5 className="font-semibold">{testimonial.name}</h5>
                      <p className="text-sm text-slate-400">{testimonial.role}</p>
                      <p className="text-xs text-primary">{testimonial.company}</p>
                    </div>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed italic">
                    "{testimonial.quote}"
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 md:px-6 py-16 md:py-24">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h3 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Workflow?
            </h3>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Join hundreds of construction professionals who save hours every week with BQ SyncSpec.
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 25px 50px rgba(0,156,166,0.4)" }}
              whileTap={{ scale: 0.95 }}
              onClick={onGetStarted}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary to-primary-dark rounded-2xl text-xl font-bold shadow-2xl hover:shadow-primary/30 transition-all"
            >
              Start Your Free Project
              <SafeIcon icon={FiArrowRight} size={20} />
            </motion.button>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mt-8 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheck} className="text-green-400" />
                <span>Free to start</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheck} className="text-green-400" />
                <span>No setup required</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheck} className="text-green-400" />
                <span>Instant results</span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="px-4 md:px-6 py-8 border-t border-white/10 text-center text-slate-400">
          <p>&copy; 2025 BQ SyncSpec. Built for Philippine Construction Professionals.</p>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;